class AnimatedStages extends HTMLElement {
  static observerPools = new Map();

  static getObserverPoolKey(options) {
    return JSON.stringify({
      threshold: options?.threshold ?? 0,
      rootMargin: options?.rootMargin ?? '0px'
    });
  }

  static getObserverPool(options) {
    const key = AnimatedStages.getObserverPoolKey(options);
    let pool = AnimatedStages.observerPools.get(key);

    if (pool) return pool;

    const targets = new Map();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const instance = targets.get(entry.target);
        instance?.handleIntersectionEntry?.(entry);
      });
    }, options);

    pool = { observer, targets };
    AnimatedStages.observerPools.set(key, pool);
    return pool;
  }

  static observeInstance(instance, options) {
    if (!instance) return;
    const pool = AnimatedStages.getObserverPool(options);
    pool.targets.set(instance, instance);
    pool.observer.observe(instance);
  }

  static unobserveInstance(instance, options) {
    if (!instance) return;

    const key = AnimatedStages.getObserverPoolKey(options);
    const pool = AnimatedStages.observerPools.get(key);
    if (!pool) return;

    pool.observer.unobserve(instance);
    pool.targets.delete(instance);

    if (pool.targets.size === 0) {
      pool.observer.disconnect();
      AnimatedStages.observerPools.delete(key);
    }
  }

  constructor() {
    super();

    const offsetAttr = this.dataset.offset;
    const bottomMargin = offsetAttr ? `-${parseFloat(offsetAttr) * 100}%` : '-50px';

    this.config = {
      easing: this.dataset.easing || 'cubic-bezier(0, 0, 0.12, 0.88)',
      once: this.dataset.once !== 'false',
      delay: parseFloat(this.dataset.delay) || 0,
      duration: parseFloat(this.dataset.duration) || 0.6,
      y: this.dataset.y || '40px',
      step: parseFloat(this.dataset.step) || 0.05,
      endClassName: 'animations-end',
      imageScale: parseFloat(this.dataset.imageScale) || 1.2,
      startDelay: parseFloat(this.dataset.startDelay) || false
    };

    this._mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    this._reducedMotion = this._mql.matches;
    this._onMotionChange = () => { this._reducedMotion = this._mql.matches; };
    this._mql.addEventListener('change', this._onMotionChange);

    this.activeAnimations = new Map();
    this.isPaused = this.hasAttribute('data-paused');
    this.manualTrigger = this.hasAttribute('data-manual');
    this.hasTriggered = false;
    this.boundResize = this.handleResize.bind(this);
    this.observerOptions = {
      threshold: 0,
      rootMargin: `20px 0px ${bottomMargin} 0px`
    };
  }

  connectedCallback() {
    if (!document.body.classList.contains('reveal-on-scroll') && !this.closest('main-loop-slider, vertical-slider')) return;

    this.updateDeviceState();

    let isEarlySection = false;

    if (!this.closest('vertical-slider')) {
      const section = this.closest('[data-section-index]');
      const sectionIndex = Number(section?.getAttribute('data-section-index'));

      const hasCatalogButton = document.querySelector('.section--mobile-catalog-button');

      isEarlySection = hasCatalogButton ? sectionIndex <= 3 : sectionIndex <= 2;
    }

    if (this.hasAttribute('data-instant') || isEarlySection) {
      this.init(true);
    } else {
      this.init();
    }

    window.addEventListener('resize', this.boundResize);
  }

  handleResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => this.updateDeviceState(), 150);
  }

  updateDeviceState() {
    const inside = this.closest('main-loop-slider') || this.closest('vertical-slider');
    this.isMobile = typeof DeviceUtils !== 'undefined' ? DeviceUtils.isMobileSmall : window.innerWidth < 768;
    this.simpleMobile = this.isMobile && !inside;
  }

  init(instant = false) {
    if (this.manualTrigger) return;
    if (this.isPaused && !instant) return;

    const start = () => {
      if (instant) {
        AnimatedStages.unobserveInstance(this, this.observerOptions);
        this.initAnimation();
        return;
      }

      AnimatedStages.observeInstance(this, this.observerOptions);
    };

    if (this.config.startDelay) {
      setTimeout(start, this.config.startDelay);
    } else {
      start();
    }
  }

  handleIntersection(entries) {
    if (this.isPaused) return;
    entries.some((entry) => this.handleIntersectionEntry(entry));
  }

  handleIntersectionEntry(entry) {
    if (this.isPaused || !entry?.isIntersecting) return false;
    this.initAnimation();
    if (this.config.once) AnimatedStages.unobserveInstance(this, this.observerOptions);
    return true;
  }

  play() {
    if (this.hasTriggered && this.config.once) return;
    this.isPaused = false;
    this.hasTriggered = true;
    AnimatedStages.unobserveInstance(this, this.observerOptions);
    this.initAnimation();
  }

  pause() {
    this.isPaused = true;
    this.activeAnimations.forEach(animation => {
      try { animation?.pause?.(); } catch {}
    });
  }

  getTime() {
    return (performance.now() / 1000).toFixed(3) + 's';
  }

  getElementId(element) {
    return element.id || element.className || element.tagName;
  }

  initAnimation(target = null) {
    const { animate, stagger } = window.motion || {};
    if (!animate) return;

    if (this._reducedMotion) {
      this._revealInstant(target);
      return;
    }

    if (target) {
      if (target.matches('[data-animate="single"]')) return this.animateElement(animate, target);
      if (target.matches('[data-animate="stages"]')) return this.animateStageContainer(animate, stagger, target);
      if (target.matches('[data-animate="image"]')) return this.animateImage(animate, target);
    } else {
      this.animateSingle(animate);
      this.animateStages(animate, stagger);
      this.animateImages(animate);
    }
  }

  _revealInstant(target = null) {
    const container = target ?? this;
    container.querySelectorAll('[data-animate]').forEach(el => {
      el.style.opacity = '';
      el.style.transform = '';
      el.classList.add(this.config.endClassName);
      if (el.dataset.hoverClass) el.classList.add(el.dataset.hoverClass);
      el.querySelectorAll('[data-animate-item]').forEach(child => {
        child.style.opacity = '';
        child.style.transform = '';
      });
      const img = el.matches('[data-animate="image"]') ? el.querySelector('img') : null;
      if (img) {
        img.style.opacity = '';
        img.style.transform = '';
      }
    });
  }

  resetAnimation(target = null) {
    if (!target) {
      this.activeAnimations.forEach(animation => {
        try { animation?.cancel?.(); } catch {}
      });
      this.activeAnimations.clear();
    } else {
      const animation = this.activeAnimations.get(target);
      if (animation) {
        try { animation.cancel(); } catch {}
        this.activeAnimations.delete(target);
      }
    }

    const container = target ? target : this;
    container.querySelectorAll('[data-animate]').forEach(el => {
      el.style.opacity = '';
      el.style.transform = '';
      el.style.removeProperty('--motion-translateY');
      el.classList.remove(this.config.endClassName);
      el.querySelectorAll('[data-animate-item]').forEach(child => {
        child.style.opacity = '';
        child.style.transform = '';
      });
    });

    if (!target && this.config.once && !this.manualTrigger) AnimatedStages.observeInstance(this, this.observerOptions);
    this.hasTriggered = false;
  }

  animateImages(animate) {
    this.querySelectorAll('[data-animate="image"]').forEach(el => this.animateImage(animate, el));
  }

  animateImage(animate, container) {
    const img = container.querySelector('img');
    if (!img) return;
    if (!img.complete) img.style.opacity = '0';

    const startAnimation = () => {
      const delay = parseFloat(container.dataset.animateDelay) || this.config.delay;
      const duration = parseFloat(container.dataset.animateDuration) || this.config.duration;

      const animation = animate(img, { opacity: [0, 1], scale: [this.config.imageScale, 1] }, { duration, delay, easing: this.config.easing });
      this.activeAnimations.set(container, animation);

      animation.finished.then(() => {
        img.style.transform = '';
        img.style.opacity = '1';
        container.classList.add(this.config.endClassName);
        if (container.dataset.hoverClass) container.classList.add(container.dataset.hoverClass);
        this.activeAnimations.delete(container);
      }).catch(() => {
        this.activeAnimations.delete(container);
      });
    };

    img.complete ? startAnimation() : img.addEventListener('load', startAnimation, { once: true });
  }

  animateSingle(animate) {
    this.querySelectorAll('[data-animate="single"]').forEach(el => this.animateElement(animate, el));
  }

  animateElement(animate, element) {
    const delay = parseFloat(element.dataset.animateDelay || this.config.delay);
    const duration = parseFloat(element.dataset.animateDuration || this.config.duration);
    const y = this.simpleMobile ? '0' : (element.dataset.animateY || this.config.y);

    const animation = animate(element, {
      opacity: [0, 1],
      ...(this.simpleMobile ? {} : { y: [y.includes('%') ? y : parseFloat(y), 0] })
    }, { duration, delay, easing: this.config.easing });

    this.activeAnimations.set(element, animation);
    animation.finished.then(() => {
      element.style.transform = '';
      this.activeAnimations.delete(element);
    }).catch(() => {
      this.activeAnimations.delete(element);
    });
  }

  animateStages(animate, stagger) {
    this.querySelectorAll('[data-animate="stages"]').forEach(container => {
      const parentStage = container.parentElement.closest('[data-animate="stages"]');
      if (parentStage && container.dataset.animateMerge !== undefined) return;
      this.animateStageContainer(animate, stagger, container);
    });
  }

  animateStageContainer(animate, stagger, container) {
    let elements = Array.from(container.querySelectorAll(':scope > *:not(script):not(style)'));
    if (!elements.length) return;

    elements = elements.filter(el => !el.closest('.hidden'));
    if (container.dataset.animateMerge !== undefined) {
      elements = this.getFlattenedChildren(container).filter(el => !el.closest('.hidden'));
    }

    const max = container.dataset.animateMax === undefined || container.dataset.animateMax === 'none' ? Infinity : parseInt(container.dataset.animateMax);
    const visibleElements = elements.slice(0, max);
    const baseDelay = parseFloat(container.dataset.animateDelay || this.config.delay);
    const stepDelay = parseFloat(container.dataset.animateStep || this.config.step);
    const duration = parseFloat(container.dataset.animateDuration || this.config.duration * 0.8);
    const y = this.simpleMobile ? '0' : (container.dataset.animateY || this.config.y);

    const animation = animate(
      visibleElements,
      {
        opacity: [0, 1],
        ...(this.simpleMobile
          ? {}
          : {
              transform: [
                `translateY(${y.includes('%') ? y : parseFloat(y) + 'px'})`,
                'translateY(0)'
              ]
            })
      },
      {
        delay: stagger(stepDelay, { from: 0, start: baseDelay }),
        duration,
        easing: this.config.easing
      }
    );

    this.activeAnimations.set(container, animation);
    animation.finished.then(() => {
      requestAnimationFrame(() => {
        container.classList.add(this.config.endClassName);
        if (container.classList.contains('product-grid')) container.classList.remove('off-transition');
        visibleElements.forEach(el => el.style.transform = '');
        this.activeAnimations.delete(container);
      });
    }).catch(() => {
      this.activeAnimations.delete(container);
    });
  }

  getFlattenedChildren(container) {
    const result = [];
    const traverse = (parent) => {
      parent.querySelectorAll(':scope > *:not(script):not(style)').forEach(child => {
        if (child.matches('[data-animate="stages"]') && child.dataset.animateMerge !== undefined) traverse(child);
        else result.push(child);
      });
    };
    traverse(container);
    return result;
  }

  disconnectedCallback() {
    AnimatedStages.unobserveInstance(this, this.observerOptions);
    clearTimeout(this.resizeTimeout);
    window.removeEventListener('resize', this.boundResize);
    this._mql?.removeEventListener('change', this._onMotionChange);
  }
}

customElements.define('animated-stages', AnimatedStages);


class AnimatedStagesSlider extends AnimatedStages {
  connectedCallback() {
    const slider = this.closest('main-loop-slider');
    const slide = this.closest('[data-index]');

    const sectionIndex = slider?.getAttribute('data-section-index');
    const slideIndex = slide?.getAttribute('data-index');

    const isFirstSlideInLateSection = sectionIndex && sectionIndex !== '1' && slideIndex === '0';

    if (isFirstSlideInLateSection) {
      this.manualTrigger = false;
      this.isPaused = false;

      super.connectedCallback();
      this.resetAnimation();
      AnimatedStages.observeInstance(this, this.observerOptions);
    } else {
      super.connectedCallback();
    }
  }

  handleIntersectionEntry(entry) {
    return super.handleIntersectionEntry(entry);
  }

  resetAnimation() {
    this.activeAnimations.forEach(animation => {
      try { animation?.cancel?.(); } catch {}
    });
    this.activeAnimations.clear();

    this.querySelectorAll('[data-animate="stages"]').forEach(container => {
      container.classList.remove(this.config.endClassName);
      const yRaw = container.dataset.animateY || this.config.y;
      const yValue = this.simpleMobile ? '0' : yRaw;
      const transformValue = `translateY(${yValue.includes('%') ? yValue : parseFloat(yValue) + 'px'})`;

      this.getFlattenedChildren(container).forEach(el => {
        el.style.opacity = '0';
        el.style.transform = transformValue;
      });
    });

    this.hasTriggered = false;
  }
}

customElements.define('animated-stages-slider', AnimatedStagesSlider);